import { ReactNode, useContext, useEffect, useId, useMemo, useState } from "react";
import { Box, Link, List, Skeleton, Typography } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import { useInterval } from "react-use";
import { Trans } from "react-i18next";
import { Lms } from "@verity/api-client/types";
import {
	getDisplayedPlatformsFromProctoringOptions,
	getProctoringPlatformName,
	ThemeWrapper,
} from "@verity/components";
import {
	CoreButton,
	CoreChip,
	CoreIconButton,
	CoreNoResults,
	CorePagination,
	CoreSearchField,
	CoreSearchFieldPaper,
	CoreTabNumberIcon,
	CoreTabs,
	createTabsContext,
} from "@verity/components/core";
import { isChromeOsDevice, isMobileDevice } from "../../../../helpers/device";
import { getLmsName } from "../../../../helpers/lms-name";
import { PortalControllerContext } from "../../../context";
import { useGetActiveLiveSessionIdsQuery, useGetAssessmentsQuery } from "./store/hooks";
import { Assessment } from "./store/slices/assessments";
import i18n from "../../../../i18n";
import useStartAssessment, { useCheckCompatibility } from "../../../start-assessment";
import {
	assessmentSessionsAvailable,
	filterRegisteredLiveSessions,
	isAssessmentActive,
	isAssessmentCompleted,
	isAssessmentScheduleRequired,
	isAssessmentUpcoming,
	isLiveSessionActive,
	isLiveSessionEnded,
	isLiveSessionRegistered,
	isLiveSessionRegistrationEnded,
} from "./filter";
import RegisterLiveSessionPage from "./register-live-session-page";
import { AssessmentFilter, AssessmentFilterButton, assessmentMatches, AssessmentPlatform } from "../assessment-filter";
import PageCard from "../../page-card";
import { useLaunchLockdownModal } from "../../launch-lockdown-modal";
import VideoPage from "../../video-page";
import VideoPopover from "../../video-popover";
import InstallExtensionModal from "../install-extension-modal";
import AssessmentInProgressModal from "../assessment-in-progress-modal";
import { isProctorModeDisabled } from "../assessments-page-helper";
import styles from "./index.module.css";
import Assessments from "../../../../images/portal/live-schedule.svg?react";
import CheckCompatibility from "../../../../images/portal/check-compatibility.svg?react";

const ALL_TABS = ["all", "active", "upcoming", "scheduleRequired", "completed"] as const;
type Tab = (typeof ALL_TABS)[number];

const PAGE_SIZE = 5;

const LIVE_SESSION_CHIP_COLOUR = "#f8e4dd";
const PLATFORM_CHIP_COLOURS = {
	extension: "#d3ddf2",
	lockdown: "#f5e5f4",
	mobile: "#e4e3f4",
	chromeOs: "#f8efe0",
};
const INTRODUCTORY_VIDEO_URLS: Record<Lms, string> = {
	BlackboardLearn: "https://demo.video.yuja.com/V/Video?v=14322859&node=62203139&a=88935565",
	canvas: "https://demo.video.yuja.com/V/Video?v=14322309&node=62200612&a=182156434",
	desire2learn: "https://demo.video.yuja.com/V/Video?v=14322299&node=62200594&a=77561416",
	moodle: "https://demo.video.yuja.com/V/Video?v=14322295&node=62200580&a=184877574",
};

function ModeChip({
	colour,
	children,
	mobileStyling,
}: {
	colour: string;
	children: ReactNode;
	mobileStyling?: boolean;
}) {
	return (
		<CoreChip className={styles.modeIndicator} chipColor={colour} mobileStyling={mobileStyling}>
			{children}
		</CoreChip>
	);
}

function AssessmentEntry({
	assessment,
	currTimestamp,
	selectLiveSession,
}: {
	assessment: Assessment;
	currTimestamp: number;
	selectLiveSession: () => void;
}) {
	const { name, proctoringOptions, liveSessions, attemptsCount } = assessment;
	const { enabled, isLiveProctored, isExtension, isLockdown, isMobile, chromeOsAccess } = proctoringOptions ?? {};
	const { userInfo } = useContext(PortalControllerContext);
	const userUuid = userInfo?.uuid;
	const inMobileDevice = isMobileDevice();

	const [assessmentError, setAssessmentError] = useState<
		| {
				action: "start" | "check-compatibility";
				reason: "in-progress";
		  }
		| undefined
	>(undefined);
	const [extensionInstallInfo, setExtensionInstallInfo] = useState<
		| {
				action: "start" | "check-compatibility";
				reason: "extension" | "incognito" | "managed-guest-session";
		  }
		| undefined
	>(undefined);
	const { modal: launchLockdownModal, setLaunchInfo: setLockdownLaunchInfo } = useLaunchLockdownModal();
	const startAssessment = useStartAssessment(
		assessment,
		(reason) => setExtensionInstallInfo({ action: "start", reason }),
		setLockdownLaunchInfo,
	);
	const checkCompatibility = useCheckCompatibility(
		assessment,
		(reason) => setExtensionInstallInfo({ action: "check-compatibility", reason }),
		setLockdownLaunchInfo,
		(reason) => setAssessmentError({ action: "check-compatibility", reason }),
	);

	const userLiveSessions = filterRegisteredLiveSessions(liveSessions, userUuid);
	const selectedLiveSession = isLiveProctored
		? (userLiveSessions.filter((session) => isLiveSessionActive(session, currTimestamp))[0] ??
			userLiveSessions.filter((session) => !isLiveSessionEnded(session, currTimestamp))[0] ??
			userLiveSessions[0])
		: undefined;

	let displayedTime = i18n.t("portal.components.studentAssessmentPageTSX.nonLiveDate");
	if (isLiveProctored) {
		if (selectedLiveSession) {
			displayedTime = `${new Date(selectedLiveSession.startTime).toLocaleDateString([], {
				month: "short",
				day: "numeric",
				year: "numeric",
			})} - ${new Date(selectedLiveSession.startTime).toLocaleTimeString([], {
				hour: "2-digit",
				minute: "2-digit",
			})}`;
		} else if (liveSessions.some((session) => !isLiveSessionRegistrationEnded(session, currTimestamp))) {
			displayedTime = i18n.t("portal.components.studentAssessmentPageTSX.scheduleRequired");
		} else if (liveSessions.some((session) => !isLiveSessionEnded(session, currTimestamp))) {
			displayedTime = i18n.t("portal.components.studentAssessmentPageTSX.allRegistrationEnded");
		} else {
			displayedTime = i18n.t("portal.components.studentAssessmentPageTSX.allSessionsEnded");
		}
	}

	const active = isAssessmentActive(assessment, userUuid, currTimestamp);
	const upcoming = isAssessmentUpcoming(assessment, userUuid, currTimestamp);
	const scheduleRequired = isAssessmentScheduleRequired(assessment, userUuid, currTimestamp);
	const completed = isAssessmentCompleted(assessment, userUuid, currTimestamp);

	const sessionsUpcoming = userLiveSessions.some((session) => !isLiveSessionEnded(session, currTimestamp));
	const sessionsFutureRegistrable = assessmentSessionsAvailable(assessment, currTimestamp);
	const canChangeSession = liveSessions.some(
		(session) => !isLiveSessionRegistered(session, userUuid) && !isLiveSessionRegistrationEnded(session, currTimestamp),
	);
	const registeredSessionsActive =
		!isLiveProctored ||
		filterRegisteredLiveSessions(liveSessions, userUuid).some((session) => isLiveSessionActive(session, currTimestamp));

	const wrongPlatformTooltip =
		isChromeOsDevice() && isLockdown && !isExtension && !chromeOsAccess
			? i18n.t("portal.components.studentAssessmentPageTSX.chromeOsDisabled")
			: i18n.t("portal.components.studentAssessmentPageTSX.wrongPlatform");

	const platformOptions = {
		isExtension: isExtension ?? undefined,
		isLockdown: isLockdown ?? undefined,
		isMobile: isMobile ?? undefined,
		isChromeOs: !!chromeOsAccess,
	};
	const platforms = getDisplayedPlatformsFromProctoringOptions(platformOptions);
	const platformChips = platforms.map((platform) => (
		<ModeChip key={platform} colour={PLATFORM_CHIP_COLOURS[platform]}>
			{getProctoringPlatformName(platform)}
		</ModeChip>
	));

	return (
		<li
			className={`${styles.assessmentEntry} ${active ? styles.active : ""} ${upcoming ? styles.upcoming : ""} ${
				scheduleRequired ? styles.scheduleRequired : ""
			} ${completed ? styles.completed : ""}`}
		>
			<div className={styles.assessmentHeader}>
				<div className={styles.assessmentInfoArea}>
					<Typography
						variant="h2"
						className={styles.assessmentName + " " + (inMobileDevice ? styles.mobileAssessmentName : "")}
						display={"flex"}
						alignItems={"center"}
						gap="5px"
						flexWrap="wrap"
					>
						{name}
						{!inMobileDevice && enabled && platformChips}
						{!inMobileDevice && isLiveProctored && (
							<ModeChip colour={LIVE_SESSION_CHIP_COLOUR}>
								{i18n.t("portal.components.studentAssessmentPageTSX.liveSession")}
							</ModeChip>
						)}
					</Typography>
					<Box
						display="flex"
						alignItems="center"
						rowGap="5px"
						columnGap={"6px"}
						flexWrap="wrap"
						fontSize={inMobileDevice ? "13px" : "15px"}
					>
						{displayedTime}
						{inMobileDevice && enabled && platformChips}
						{inMobileDevice && isLiveProctored && (
							<ModeChip colour={LIVE_SESSION_CHIP_COLOUR}>
								{i18n.t("portal.components.studentAssessmentPageTSX.liveSession")}
							</ModeChip>
						)}
					</Box>
				</div>
				<div className={styles.assessmentActions}>
					{(!isLiveProctored || registeredSessionsActive) && (
						<CoreButton
							disabled={!startAssessment}
							disabledTooltip={wrongPlatformTooltip}
							onClick={startAssessment}
							fullWidth={!inMobileDevice}
							sx={inMobileDevice ? { paddingX: "20px" } : { flex: 1 }}
							slotProps={{ tooltipWrapper: { style: { flex: 1 } } }}
							aria-label={
								attemptsCount > 0
									? i18n.t("portal.components.studentAssessmentPageTSX.reattemptButtonAriaLabel", {
											assessment: name,
										})
									: i18n.t("portal.components.studentAssessmentPageTSX.startButtonAriaLabel", {
											assessment: name,
										})
							}
						>
							{attemptsCount > 0
								? i18n.t("portal.components.studentAssessmentPageTSX.reattempt")
								: i18n.t("portal.components.studentAssessmentPageTSX.start")}
						</CoreButton>
					)}
					{isLiveProctored && !registeredSessionsActive && (sessionsUpcoming || sessionsFutureRegistrable) && (
						<CoreButton
							disabled={!canChangeSession}
							disabledTooltip={
								sessionsFutureRegistrable
									? i18n.t("portal.components.studentAssessmentPageTSX.noAvailableSessions")
									: i18n.t("portal.components.studentAssessmentPageTSX.registrationEnded")
							}
							onClick={selectLiveSession}
							fullWidth
							sx={{ flex: 1 }}
							slotProps={{ tooltipWrapper: { style: { flex: 1 } } }}
						>
							{selectedLiveSession
								? i18n.t("portal.components.studentAssessmentPageTSX.editSchedule")
								: i18n.t("portal.components.studentAssessmentPageTSX.schedule")}
						</CoreButton>
					)}
					{enabled && !inMobileDevice && (!isLiveProctored || sessionsUpcoming || sessionsFutureRegistrable) && (
						<CoreIconButton
							disabled={!checkCompatibility}
							disabledTooltip={wrongPlatformTooltip}
							onClick={checkCompatibility}
							tooltip={i18n.t("portal.components.studentAssessmentPageTSX.checkCompatibility")}
							aria-label={i18n.t("portal.components.studentAssessmentPageTSX.compatibilityAriaLabel", {
								assessment: name,
							})}
						>
							<SvgIcon>
								<CheckCompatibility />
							</SvgIcon>
						</CoreIconButton>
					)}
				</div>
			</div>
			{/** Ideally, check assessmentError.action in the title & body to determine which text to use */}
			{assessmentError && assessmentError.reason === "in-progress" && (
				<AssessmentInProgressModal
					onClose={() => setAssessmentError(undefined)}
					title={i18n.t("portal.components.assessmentInProgress.compatibilityTitle")}
					body={i18n.t("portal.components.assessmentInProgress.compatibilityBody")}
				/>
			)}
			{extensionInstallInfo && (
				<InstallExtensionModal
					onClose={() => setExtensionInstallInfo(undefined)}
					reason={extensionInstallInfo.reason}
					action={extensionInstallInfo.action}
					assessment={assessment}
				/>
			)}
			{launchLockdownModal}
		</li>
	);
}

function AssessmentEntrySkeleton() {
	return <Skeleton variant="rounded" className={styles.assessmentEntrySkeleton} />;
}

function AssessmentsTabContent({
	tab,
	assessments,
	initialLoading,
	loadingIds,
	currTimestamp,
	selectLiveSession,
	tabsComponent,
}: {
	tab: Tab;
	assessments: Assessment[];
	initialLoading: boolean;
	loadingIds: number[];
	currTimestamp: number;
	selectLiveSession: (assessmentId: number) => void;
	tabsComponent?: ReactNode;
}) {
	const { browserType, portalContext } = useContext(PortalControllerContext);
	const { lms } = portalContext;
	let initialPlatformFilter: AssessmentPlatform[] = [];
	if (browserType === "desktop-lockdown") {
		initialPlatformFilter = ["lockdown"];
	} else if (browserType === "mobile-browser" || browserType === "mobile-lockdown") {
		initialPlatformFilter = ["mobile"];
	} else if (browserType === "chromeos-browser") {
		initialPlatformFilter = ["extension", "chromeOs"];
	}
	const isMobile = isMobileDevice();

	const [filter, setFilter] = useState<AssessmentFilter>({ type: [], method: [], platform: initialPlatformFilter });
	const [searchFilter, setSearchFilter] = useState("");
	const { getTabIdByType } = useAssessmentTabContext();
	const controlId = useId();

	const lowerSearchFilter = searchFilter.toLowerCase();
	const filteredAssessments = useMemo(
		() =>
			assessments.filter((assessment) => {
				return (
					(!searchFilter || assessment.name?.toLowerCase().includes(lowerSearchFilter)) &&
					assessmentMatches(filter, assessment, isMobile) &&
					// dont show students assessments they are not proctored for (execpt if it's via accomodations)
					// if proctoringAssessmentConfiguration doesn't exist check proctoringOptions
					((assessment.proctoringAssessmentConfiguration && assessment.proctoringAssessmentConfiguration.enabled) ||
						(!assessment.proctoringAssessmentConfiguration && assessment.proctoringOptions?.enabled))
				);
			}),
		[assessments, searchFilter, filter],
	);

	const [page, setPage] = useState(0);
	useEffect(() => {
		setPage(0);
	}, [searchFilter, filter]);
	useEffect(() => {
		if (page * PAGE_SIZE > filteredAssessments.length) {
			setPage(Math.floor(filteredAssessments.length / PAGE_SIZE));
		}
	}, [filteredAssessments]);

	const pageStartIndex = page * PAGE_SIZE;
	const paginatedAssessments = filteredAssessments.slice(pageStartIndex, pageStartIndex + PAGE_SIZE);
	const paginatedLoadingIds =
		paginatedAssessments.length < PAGE_SIZE
			? loadingIds.slice(pageStartIndex, pageStartIndex + (PAGE_SIZE - paginatedAssessments.length))
			: [];

	const noResultsKey = assessments.length === 0 ? (`noAssessments.${tab}` as const) : "noResults";
	const isLoading = initialLoading || loadingIds.length > 0;

	return (
		<>
			{isMobile ? (
				<>
					<Box display="flex" alignItems="center" justifyContent={"space-between"}>
						<Typography variant="h1" fontSize="24px" fontWeight="bold">
							{i18n.t("portal.components.studentAssessmentPageTSX.title")}
						</Typography>
						<AssessmentFilterButton
							filter={filter}
							setFilter={setFilter}
							typeOptions={["lms", "third-party", "verityAssignment", "verityQuiz"]}
							platformOptions={["lockdown", "extension", "mobile", "chromeOs"]}
							alternateIcon
						/>
					</Box>
					<div className={styles.searchRow}>
						<CoreSearchFieldPaper>
							<CoreSearchField
								mobile={true}
								onSearch={setSearchFilter}
								ariaControls={controlId}
								totalResults={filteredAssessments.length}
								label={i18n.t("portal.components.studentAssessmentPageTSX.searchAssessments")}
								value={searchFilter}
							/>
						</CoreSearchFieldPaper>
					</div>
					{tabsComponent}
					<div role="tabpanel" aria-labelledby={getTabIdByType(tab)} style={{ display: "block" }} id={controlId}>
						{!isLoading && filteredAssessments.length === 0 && (
							<CoreNoResults
								header={i18n.t(`portal.components.studentAssessmentPageTSX.${noResultsKey}.title`)}
								headerVariant={"h2"}
							>
								{i18n.t(`portal.components.studentAssessmentPageTSX.${noResultsKey}.description`)}
							</CoreNoResults>
						)}
						{(isLoading || filteredAssessments.length > 0) && (
							<List className={styles.assessmentsList}>
								{initialLoading && (
									<>
										<AssessmentEntrySkeleton />
										<AssessmentEntrySkeleton />
										<AssessmentEntrySkeleton />
										<AssessmentEntrySkeleton />
										<AssessmentEntrySkeleton />
									</>
								)}
								{!initialLoading &&
									filteredAssessments.map((assessment) => (
										<AssessmentEntry
											key={assessment.id}
											assessment={assessment}
											currTimestamp={currTimestamp}
											selectLiveSession={() => selectLiveSession(assessment.id)}
										/>
									))}
								{!initialLoading && paginatedLoadingIds.map((id) => <AssessmentEntrySkeleton key={id} />)}
							</List>
						)}
					</div>
				</>
			) : (
				<>
					{tabsComponent}
					<div role="tabpanel" aria-labelledby={getTabIdByType(tab)} style={{ display: "contents" }} id={controlId}>
						<Typography className={styles.description}>
							{i18n.t(`portal.components.studentAssessmentPageTSX.description.${tab}`)}
						</Typography>
						{lms && (
							<VideoPopover
								src={INTRODUCTORY_VIDEO_URLS[lms]}
								videoTitle={i18n.t("portal.components.studentAssessmentPageTSX.introductoryVideo.videoTitle", {
									lmsName: getLmsName(lms),
								})}
								linkText={i18n.t("portal.components.studentAssessmentPageTSX.introductoryVideo.linkText")}
								header={i18n.t("portal.components.studentAssessmentPageTSX.introductoryVideo.popover.header")}
								description={
									<Trans
										i18nKey="portal.components.studentAssessmentPageTSX.introductoryVideo.popover.description"
										components={{
											a: (
												<Link
													href="https://support.yuja.com/hc/en-us/sections/31910820512535-Taking-a-Proctored-Assessment"
													target="_blank"
													rel="noopener noreferrer"
													color="#0B6F85"
												/>
											),
										}}
									/>
								}
							/>
						)}
						<CoreSearchFieldPaper>
							<AssessmentFilterButton
								filter={filter}
								setFilter={setFilter}
								typeOptions={["lms", "third-party", "verityAssignment", "verityQuiz"]}
								platformOptions={["lockdown", "extension", "mobile", "chromeOs"]}
							/>
							<CoreSearchField
								mobile={false}
								onSearch={setSearchFilter}
								ariaControls={controlId}
								totalResults={filteredAssessments.length}
								label={i18n.t("portal.components.studentAssessmentPageTSX.searchAssessments")}
								value={searchFilter}
							/>
						</CoreSearchFieldPaper>
						{!isLoading && filteredAssessments.length === 0 && (
							<CoreNoResults
								header={i18n.t(`portal.components.studentAssessmentPageTSX.${noResultsKey}.title`)}
								headerVariant={"h2"}
							>
								{i18n.t(`portal.components.studentAssessmentPageTSX.${noResultsKey}.description`)}
							</CoreNoResults>
						)}
						{(isLoading || filteredAssessments.length > 0) && (
							<List className={styles.assessmentsList}>
								{initialLoading && (
									<>
										<AssessmentEntrySkeleton />
										<AssessmentEntrySkeleton />
										<AssessmentEntrySkeleton />
										<AssessmentEntrySkeleton />
										<AssessmentEntrySkeleton />
									</>
								)}
								{!initialLoading &&
									paginatedAssessments.map((assessment) => (
										<AssessmentEntry
											key={assessment.id}
											assessment={assessment}
											currTimestamp={currTimestamp}
											selectLiveSession={() => selectLiveSession(assessment.id)}
										/>
									))}
								{!initialLoading && paginatedLoadingIds.map((id) => <AssessmentEntrySkeleton key={id} />)}
							</List>
						)}
						{!initialLoading && filteredAssessments.length > PAGE_SIZE && (
							<CorePagination
								className={styles.pagination}
								count={Math.ceil(filteredAssessments.length / PAGE_SIZE)}
								page={page + 1}
								onChange={(_, newPage) => setPage(newPage - 1)}
							/>
						)}
					</div>
				</>
			)}
		</>
	);
}

export const { TabsProvider: AssessmentTabProvider, useTabContext: useAssessmentTabContext } = createTabsContext<Tab>();

export default function StudentAssessmentsPage() {
	const { portalContext, userInfo, verityInstituteSettings, dismissableUiElements, dismissUiElement } =
		useContext(PortalControllerContext);
	const { lms } = portalContext;
	const inMobileDevice = isMobileDevice();
	const [currTimestamp, setCurrTimestamp] = useState(Date.now());
	useInterval(() => setCurrTimestamp(Date.now()), 20000);
	const { data, isLoading: initialLoading, isError } = useGetAssessmentsQuery(portalContext);

	// only need to check live sessions between (start time - 15 minutes) and start time since they will be startable anyways after the start time
	const liveSessionIdsToCheckActive = useMemo(
		() =>
			data
				? Object.values(data.map)
						.flatMap(({ liveSessions }) => liveSessions)
						.filter(({ startTime }) => {
							const startTimestamp = new Date(startTime).getTime();
							return currTimestamp >= startTimestamp - 15 * 60 * 1000 && currTimestamp <= startTimestamp;
						})
						.map(({ id }) => id)
				: [],
		[data, currTimestamp],
	);
	const { data: activeLiveSessionIds } = useGetActiveLiveSessionIdsQuery(liveSessionIdsToCheckActive, {
		pollingInterval: 20000,
	});

	const assessments: Record<string, Assessment> | undefined = useMemo(() => {
		const activeLiveSessionIdsSet = new Set(activeLiveSessionIds);
		return data
			? Object.fromEntries(
					Object.entries(data.map).map(([id, assessment]) => [
						id,
						{
							...assessment,
							liveSessions: assessment.liveSessions.map((session) => ({
								...session,
								started: activeLiveSessionIdsSet.has(session.id) ?? false,
							})),
						},
					]),
				)
			: undefined;
	}, [data, activeLiveSessionIds]);

	const [tab, setTab] = useState<Tab>("all");
	const [registerSessionAssessmentId, setRegisterSessionAssessmentId] = useState<number | undefined>(undefined);

	const { addTab, getTabIdByType } = useAssessmentTabContext();

	// Register Tab IDs
	useEffect(() => {
		ALL_TABS.forEach((key) => {
			addTab(key);
		});
	}, [addTab]);

	const assessmentsList = useMemo(
		() =>
			data && assessments
				? data.ids
						.filter((id) => !isProctorModeDisabled(verityInstituteSettings, data.map[id].proctoringOptions))
						.map((id) => assessments[id])
				: [],
		[data, assessments, verityInstituteSettings],
	);
	const assessmentsByType: Record<Tab, Assessment[]> = useMemo(
		() => ({
			all: assessmentsList,
			active: assessmentsList.filter((assessment) => isAssessmentActive(assessment, userInfo?.uuid, currTimestamp)),
			upcoming: assessmentsList.filter((assessment) => isAssessmentUpcoming(assessment, userInfo?.uuid, currTimestamp)),
			scheduleRequired: assessmentsList.filter((assessment) =>
				isAssessmentScheduleRequired(assessment, userInfo?.uuid, currTimestamp),
			),
			completed: assessmentsList.filter((assessment) =>
				isAssessmentCompleted(assessment, userInfo?.uuid, currTimestamp),
			),
		}),
		[assessmentsList, userInfo, currTimestamp],
	);

	const showAllAssessmentsTab =
		!verityInstituteSettings ||
		verityInstituteSettings.showAllAssessments === undefined ||
		verityInstituteSettings.showAllAssessments;
	useEffect(() => {
		if (!showAllAssessmentsTab && tab === "all") {
			setTab("active");
		}
	}, [showAllAssessmentsTab]);

	const body = (
		<div className={styles.cardContent}>
			{isError ? (
				<CoreNoResults
					variant="failed"
					header={i18n.t("portal.components.studentAssessmentPageTSX.errorLoading.title")}
					headerVariant={"h2"}
				>
					{i18n.t("portal.components.studentAssessmentPageTSX.errorLoading.description")}
				</CoreNoResults>
			) : (
				<AssessmentsTabContent
					key={tab}
					tab={tab}
					assessments={assessmentsByType[tab]}
					initialLoading={initialLoading}
					loadingIds={data?.loadingIds ?? []}
					currTimestamp={currTimestamp}
					selectLiveSession={setRegisterSessionAssessmentId}
					tabsComponent={
						<CoreTabs
							secondary
							value={tab}
							onChange={(_, newTab) => setTab(newTab)}
							tabs={(showAllAssessmentsTab ? ALL_TABS : ALL_TABS.slice(1)).map((tab) => ({
								key: tab,
								...(inMobileDevice
									? {} // Exclude icon on mobile
									: {
											icon: (
												<CoreTabNumberIcon
													count={assessmentsByType[tab].length}
													isLoading={
														initialLoading ||
														!data ||
														(data.loadingIds.length > 0 && assessmentsByType[tab].length < 100)
													}
												/>
											),
										}),
								label: i18n.t(`portal.components.studentAssessmentPageTSX.tab.${tab}`),
								id: getTabIdByType(tab),
							}))}
							width="fit-content"
							tabProps={
								inMobileDevice
									? {
											sx: { minHeight: "32px", fontSize: "13px", alignContent: "center" },
										}
									: {}
							}
							tabsProps={
								inMobileDevice
									? {
											sx: { minHeight: "36px", alignItems: "center" },
										}
									: {}
							}
							scrollable
						/>
					}
				/>
			)}
		</div>
	);

	if (lms && dismissableUiElements.has("introVideo"))
		return (
			<VideoPage
				src={INTRODUCTORY_VIDEO_URLS[lms]}
				videoTitle={i18n.t("portal.components.studentAssessmentPageTSX.introductoryVideo.videoTitle", {
					lmsName: getLmsName(lms),
				})}
				header={i18n.t("portal.components.studentAssessmentPageTSX.introductoryVideo.header")}
				description={
					<Trans
						i18nKey={`portal.components.studentAssessmentPageTSX.introductoryVideo.${
							!initialLoading && assessmentsList.length === 0 ? "descriptionNoAssessments" : "description"
						}`}
						components={{
							a: (
								<Link
									href="https://support.yuja.com/hc/en-us/sections/31910820512535-Taking-a-Proctored-Assessment"
									target="_blank"
									rel="noopener noreferrer"
									color="#0B6F85"
								/>
							),
						}}
					/>
				}
				bottomContent={
					<CoreButton onClick={() => dismissUiElement("introVideo")} style={{ fontSize: "15px" }}>
						{i18n.t("portal.components.studentAssessmentPageTSX.introductoryVideo.button")}
					</CoreButton>
				}
			/>
		);

	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: styles.page }}>
			<div className={styles.pageContainer}>
				{registerSessionAssessmentId && assessments ? (
					<RegisterLiveSessionPage
						assessment={assessments[registerSessionAssessmentId]}
						onBack={() => setRegisterSessionAssessmentId(undefined)}
						onFinish={(selectedSession) => {
							setRegisterSessionAssessmentId(undefined);
							if (selectedSession && tab !== "all") {
								if (isLiveSessionActive(selectedSession)) {
									setTab("active");
								} else {
									setTab("upcoming");
								}
							}
						}}
					/>
				) : inMobileDevice ? (
					<Box
						display={"flex"}
						flexDirection={"column"}
						alignItems={"center"}
						gap="1px"
						bgcolor={"#FFF"}
						height={"100%"}
					>
						{body}
					</Box>
				) : (
					<PageCard
						icon={<Assessments />}
						title={i18n.t("portal.components.studentAssessmentPageTSX.title")}
						style={{ minHeight: "min-content", overflow: "unset", margin: 19 }}
						titleVariant="h1"
					>
						{body}
					</PageCard>
				)}
			</div>
		</ThemeWrapper>
	);
}
